import Box from '@mui/material/Box';

import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';

import ConfirmShipment from '../ConfirmShipment';
import NumberSteps from '../NumberSteps';
import AddUnits from './dependencies/AddUnits';
import LabelYourProducts from './dependencies/LabelYourProducts';
import PickupDetails from './dependencies/PickupDetails';
import WhenToSend from './dependencies/WhenToSend';
import useSendToWarehouse from './useSendToWarehouse';

interface SendToWarehouseProps {
  products: ProductResponse['products'];
  onClose: () => void;
}

const SendToWarehouse = ({ products, onClose }: SendToWarehouseProps) => {
  const hook = useSendToWarehouse({ onClose, products });
  const confirmationStep = hook.requiresHomePickup ? 5 : 4;

  const stepTitles = hook.requiresHomePickup
    ? [
        'Agrega cuántas unidades vas a enviar',
        'Elige cuándo enviar tus productos',
        'Etiqueta tus productos y bultos',
        'Datos de recolección',
      ]
    : [
        'Agrega cuántas unidades vas a enviar',
        'Elige cuándo enviar tus productos',
        'Etiqueta tus productos y bultos',
      ];

  return (
    <Box sx={{ width: '100%' }}>
      {hook.currentStep < confirmationStep && (
        <NumberSteps step={hook.currentStep} titles={stepTitles} />
      )}
      {hook.currentStep === 1 && (
        <AddUnits
          step={hook.currentStep}
          setCurrentStep={hook.setCurrentStep}
          products={products}
          errors={hook.errors}
          onChange={hook.onChangeProducts}
          isDisabled={hook.isStepDisabled(1)}
          productItems={hook.getValues('products')}
          editStep={hook.editStep}
          warehouse={hook.warehouse}
          onWarehouseChange={hook.onWarehouseChange}
          warehouseOptions={hook.warehouseOptions}
          isLoadingWarehouses={hook.isLoadingWarehouses}
          requiresHomePickup={hook.requiresHomePickup}
        />
      )}
      {hook.currentStep === 2 && (
        <WhenToSend
          step={hook.currentStep}
          setCurrentStep={hook.setCurrentStep}
          control={hook.control}
          errors={hook.errors}
          editStep={hook.editStep}
          isDisabled={hook.isStepDisabled(2)}
          selectedWarehouse={hook.selectedWarehouse}
          requiresHomePickup={hook.requiresHomePickup}
        />
      )}
      {hook.currentStep === 3 && (
        <LabelYourProducts
          step={hook.currentStep}
          setCurrentStep={hook.setCurrentStep}
          register={hook.register}
          errors={hook.errors}
          isDisabled={hook.isStepDisabled(3)}
          control={hook.control}
          handleChangeSelectedCountryPhone={hook.handleChangeSelectedCountryPhone}
          requiresHomePickup={hook.requiresHomePickup}
          editStep={hook.editStep}
          defaultCountryPhone={hook.countryCode}
        />
      )}
      {hook.currentStep === 4 && hook.requiresHomePickup && (
        <PickupDetails
          step={hook.currentStep}
          setCurrentStep={hook.setCurrentStep}
          control={hook.control}
          register={hook.register}
          errors={hook.errors}
          touchedFields={hook.touchedFields}
          setValue={hook.setValue}
          watch={hook.watch}
          trigger={hook.trigger}
          editStep={hook.editStep}
          isDisabled={hook.isStepDisabled(4)}
          isAddressConfirmed={hook.isAddressConfirmed}
          setIsAddressConfirmed={hook.setIsAddressConfirmed}
          isAddressTouched={hook.isAddressTouched}
          setIsAddressTouched={hook.setIsAddressTouched}
          userCountry={hook.userCountry}
          userCountryCode={hook.userCountryCode}
        />
      )}
      {hook.currentStep === confirmationStep && (
        <ConfirmShipment
          create={hook.handleSubmit(hook.createArrivalOrder, hook.onError)}
          isPending={hook.isPending}
          data={hook.drawerData}
        />
      )}
    </Box>
  );
};

export default SendToWarehouse;
